import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  MarkerType,
  ReactFlowProvider,
  type Node,
  type Edge,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { onDashboardChanged } from "@/lib/dashboardWs";
import {
  readBuckets,
  runnerReadiness,
  type RolloutBucket,
  type RunnerReadiness,
} from "@/lib/runnerReadiness";
import { WarmBucketList } from "@/components/fleet/WarmBucketList";

/**
 * Fleet — Flow view (fleet-health-dashboard-plan.md §4.4/§4.7).
 *
 * The "adjusts automatically" requirement is met by keeping RELATIONSHIPS
 * static per component KIND (DEPENDENCY_RULES below) and resolving
 * INSTANCES + PLACEMENT from live data at render time:
 *
 *   boxes        = /v1/fleet/topology .boxes   (every deploy-agent, + the
 *                                              synthetic cloudflare box)
 *   instances    = entities the hub derived a state for
 *                ∪ installed_components (installed but silent → unknown)
 *                ∪ component_locations (registered placement)
 *   edges        = DEPENDENCY_RULES[kind] × live instances of the target kind
 *
 * A service moving box-to-box therefore redraws itself; a new box appears
 * as a new row. Nothing here lists hosts. Layout follows the old
 * InfrastructurePage: deterministic rows (one per box), zone rectangles,
 * edges drawn only for the selected node so the canvas reads as a clean
 * map until you ask a question of it.
 *
 * Types mirror FleetPage.tsx / fleetHealth.ts — kept local so this page
 * can't drift silently when the other is edited.
 */
type HealthState = "up" | "degraded" | "down" | "unknown";
interface FleetEntity {
  entity: string;
  state: HealthState;
  since: string | null;
  detail: unknown;
}
interface FleetBox {
  box_id: string;
  hostname: string;
  connected: boolean;
  last_seen_ago_s: number | null;
  installed_components: Record<string, string>;
  state: HealthState;
  entities: FleetEntity[];
  /** "macos" = the macOS fleet host (its agent heartbeat is the macos-tester service, not a deploy-agent). */
  kind?: "linux" | "macos" | null;
}
interface FleetCapability {
  name: string;
  state: HealthState;
  reason: string;
}
interface Location {
  component: string;
  host: string;
  port: number;
}
interface Topology {
  ok: true;
  generated_at: string;
  boxes: FleetBox[];
  capabilities: FleetCapability[];
  incidents: number;
  locations: Location[];
}

const RANK: Record<HealthState, number> = {
  down: 3,
  degraded: 2,
  unknown: 1,
  up: 0,
};
function worst(states: HealthState[]): HealthState {
  return states.reduce<HealthState>(
    (acc, s) => (RANK[s] > RANK[acc] ? s : acc),
    "up",
  );
}

/* ── Kinds & rules ──────────────────────────────────────────────────────── */

type EdgeKind = "data" | "control" | "trust";
interface Rule {
  to: string;
  kind: EdgeKind;
  label: string;
}

/** Relationships per component kind. Changes only when the PLATFORM gains
 *  a new kind of relationship — boxes and placements never touch this. */
const DEPENDENCY_RULES: Record<string, Rule[]> = {
  runner: [
    { to: "nats", kind: "data", label: "pull jobs · publish events" },
    {
      to: "registry-internal",
      kind: "data",
      label: "npm install @pure/compiler",
    },
    { to: "auth-broker", kind: "data", label: "presigned R2 (JWT-SVID)" },
    { to: "spire-agent", kind: "trust", label: "workload SVID" },
  ],
  "deploy-agent": [
    { to: "nats", kind: "control", label: "deploy stream · heartbeat" },
    {
      to: "auth-broker",
      kind: "control",
      label: "AgentHub WebSocket · telemetry",
    },
    { to: "spire-agent", kind: "trust", label: "workload SVID" },
  ],
  // The macOS fleet host: jobs and results travel over NATS (JetStream, per-host grants from the
  // broker: its SPIFFE identity IS the attestation); the broker only carries the AgentHub
  // WebSocket (heartbeat + operator ops) and the signed release probe.
  "macos-tester": [
    { to: "nats", kind: "data", label: "jobs in · results out (JetStream)" },
    {
      to: "auth-broker",
      kind: "control",
      label: "AgentHub WebSocket · operator ops (JWT-SVID)",
    },
    { to: "spire-agent", kind: "trust", label: "own spire-agent · x509pop" },
    {
      to: "registry-internal",
      kind: "data",
      label: "signed release (via the broker)",
    },
  ],
  "mgmt-actions": [{ to: "nats", kind: "control", label: "actions stream" }],
  nats: [
    { to: "spire-agent", kind: "trust", label: "server SVID · NATS JWT auth" },
  ],
  caddy: [
    { to: "nats", kind: "data", label: "TLS passthrough / WS" },
    { to: "spire-oidc", kind: "data", label: "oidc.<domain>" },
  ],
  "spire-agent": [
    {
      to: "spire-server",
      kind: "trust",
      label: "x509pop attest · fetch SVIDs",
    },
  ],
  "spire-oidc": [
    { to: "spire-server", kind: "trust", label: "JWKS source (bundle)" },
  ],
  "spire-server": [
    { to: "openbao", kind: "trust", label: "UpstreamAuthority" },
    { to: "spire-postgres", kind: "data", label: "datastore" },
  ],
  api: [
    { to: "nats", kind: "data", label: "submit jobs · subscribe events" },
    { to: "auth-broker", kind: "data", label: "AUTH_BROKER binding" },
  ],
  app: [{ to: "api", kind: "data", label: "jobs API" }],
  management: [
    { to: "auth-broker", kind: "control", label: "/v1/fleet/* · relay" },
  ],
  "auth-broker": [
    { to: "spire-oidc", kind: "trust", label: "verify JWT-SVID (JWKS)" },
    {
      to: "registry-internal",
      kind: "control",
      label: "REGISTRY_INTERNAL binding",
    },
    { to: "api", kind: "control", label: "JobsAdmin binding" },
  ],
  "image-registry": [],
  registry: [],
  "registry-internal": [],
  openbao: [],
  "spire-postgres": [],
  minio: [],
};

/** Display metadata per kind; unknown kinds fall back to the key itself. */
const KIND_META: Record<string, { label: string; zone: Zone }> = {
  runner: { label: "runner", zone: "execution" },
  "deploy-agent": { label: "deploy-agent", zone: "control" },
  "macos-tester": { label: "macos-tester (Mac)", zone: "execution" },
  "mgmt-actions": { label: "mgmt-actions", zone: "control" },
  nats: { label: "NATS / JetStream", zone: "data" },
  caddy: { label: "Caddy", zone: "public" },
  minio: { label: "MinIO", zone: "data" },
  "spire-agent": { label: "spire-agent", zone: "identity" },
  "spire-server": { label: "spire-server", zone: "identity" },
  "spire-oidc": { label: "spire-oidc", zone: "identity" },
  "spire-postgres": { label: "spire-postgres", zone: "identity" },
  openbao: { label: "OpenBao", zone: "identity" },
  api: { label: "pure-api", zone: "public" },
  app: { label: "pure-app", zone: "public" },
  management: { label: "management", zone: "public" },
  "auth-broker": { label: "auth-broker", zone: "control" },
  registry: { label: "registry", zone: "public" },
  "registry-internal": { label: "registry-internal", zone: "data" },
  "image-registry": { label: "image-registry", zone: "data" },
};
type Zone = "public" | "control" | "execution" | "data" | "identity";

/** Name normalisation: deploy component names, systemd units, entity
 *  prefixes and Worker hostnames all end up as one kind vocabulary. */
const KIND_ALIASES: Record<string, string> = {
  queue: "nats",
  "nats-cluster": "nats",
  proxy: "caddy",
  spire: "spire-server",
  "spire-server.service": "spire-server",
  "spire-server-2.service": "spire-server",
  "spire-agent.service": "spire-agent",
  "pure-deploy-agent.service": "deploy-agent",
  "pure-mgmt-actions.service": "mgmt-actions",
  "pure-runner.service": "runner",
  // apt package names of the host services.
  "pure-runner": "runner",
  "pure-deploy-agent": "deploy-agent",
  "api.pure.dev": "api",
  "auth.pure.dev": "auth-broker",
  "registry.pure.dev": "registry",
  "images.pure.dev": "image-registry",
  "registry-internal.pure.dev": "registry-internal",
  "management.pure.dev": "management",
};
const kindOf = (name: string) => KIND_ALIASES[name] ?? name;

/* ── Instances from live data ───────────────────────────────────────────── */

/** Per-bucket warm-target progress — internal/runner's WarmBucketStatus
 *  (sandbox/warmTargetPrewarm.ts: warm | prewarming | pending | failed, plus
 *  `lastFailure`), passed through unmodified by fleetHealth.ts's
 *  deriveStates and validated by readBuckets. */
type WarmBucketStatus = RolloutBucket;
interface RunnerJob {
  jobId: string;
}
interface Instance {
  id: string;
  box: FleetBox;
  kind: string;
  /** Instance key within the kind on this box (runner id, unit name…). */
  key: string;
  state: HealthState;
  entities: FleetEntity[];
  source: "telemetry" | "installed" | "location";
  /** Runner occupancy (direct operator request, 2026-09-21: "ready for
   *  jobs / prewarming / processing a job") — only ever set for
   *  `kind === "runner"`, read from that runner's own `runner:<id>`
   *  entity detail (fleetHealth.ts's deriveStates, "runner-status" case).
   *  `undefined` for every other kind, or a runner an older build hasn't
   *  reported these fields for yet. */
  occupancy?: "ready" | "prewarming" | "processing" | "unknown";
  job?: RunnerJob | null;
  warmTargets?: WarmBucketStatus[];
  /** Which compiler version `warmTargets` describes (the staged one during
   *  a rollout / failed-rollout backoff); null on an older runner. */
  warmTargetsVersion?: string | null;
  /** Job admission readiness (lib/runnerReadiness.ts) — the primary
   *  "accepting jobs?" signal; null/undefined for an older runner build
   *  without `admission` (then occupancy is used, as before). */
  readiness?: RunnerReadiness | null;
}

function instancesFor(box: FleetBox): Instance[] {
  const byKey = new Map<string, Instance>();
  const add = (
    kind: string,
    key: string,
    e: FleetEntity | null,
    source: Instance["source"],
  ) => {
    const id = `${box.box_id}/${kind}/${key}`;
    let inst = byKey.get(id);
    if (!inst) {
      inst = { id, box, kind, key, state: "unknown", entities: [], source };
      byKey.set(id, inst);
    }
    if (e) inst.entities.push(e);
    return inst;
  };
  // Entities that describe a component directly.
  const runners = box.entities.filter((e) => e.entity.startsWith("runner:"));
  for (const e of box.entities) {
    const [prefix, ...rest] = e.entity.split(":");
    const rem = rest.join(":");
    switch (prefix) {
      case "runner":
        add("runner", rem, e, "telemetry");
        break;
      case "identity":
        // The runner's identity block belongs to that runner's node.
        add("runner", rem, e, "telemetry");
        break;
      case "nats-cluster":
        add("nats", rem, e, "telemetry");
        break;
      case "worker":
        add(kindOf(rem), rem, e, "telemetry");
        break;
      case "box":
        // On the Mac the agent channel IS the macos-tester service.
        if (box.kind === "macos") add("macos-tester", "agent", e, "telemetry");
        else add("deploy-agent", "agent", e, "telemetry");
        break;
      case "macos-tester":
        add("macos-tester", "agent", e, "telemetry");
        break;
      case "identity-probe":
        add("spire-agent", "spire-agent.service", e, "telemetry");
        break;
      case "nats-reach":
        // "The queue answers from this box" — evidence on the agent's
        // own connection, so it belongs to the deploy-agent node.
        add("deploy-agent", "agent", e, "telemetry");
        break;
      case "unit": {
        // The aggregate pure-runner.service is redundant once per-runner
        // status exists; keep it only as a fallback.
        if (rem === "pure-runner.service" && runners.length > 0) break;
        const kind = kindOf(rem);
        const key = kind === "deploy-agent" ? "agent" : rem;
        add(kind, key, e, "telemetry");
        break;
      }
      case "cert": {
        // Certs attach to the component that serves them.
        const role = rem.replace(/-ca$/, "");
        const kind =
          role === "nats"
            ? "nats"
            : role === "spire-oidc"
              ? "spire-oidc"
              : role === "deploy-agent"
                ? "deploy-agent"
                : "caddy";
        const key =
          kind === "deploy-agent"
            ? "agent"
            : kind === "spire-oidc"
              ? "spire-oidc"
              : kind;
        add(kind, key, e, "telemetry");
        break;
      }
      case "timer":
        // Renewal/backup timers attach to what they renew/back up.
        if (rem.includes("svid") || rem.includes("queue"))
          add("nats", "nats", e, "telemetry");
        else if (rem.includes("openbao"))
          add("openbao", "openbao", e, "telemetry");
        else if (rem.includes("minio")) add("minio", "minio", e, "telemetry");
        else if (rem.includes("jetstream")) add("nats", "nats", e, "telemetry");
        else add("deploy-agent", "agent", e, "telemetry");
        break;
      default:
        break;
    }
  }
  // Installed but silent: still a node, shown unknown with the reason.
  for (const name of Object.keys(box.installed_components)) {
    const kind = kindOf(name);
    if (kind === "runner" && runners.length > 0) continue;
    if (![...byKey.values()].some((i) => i.kind === kind))
      add(kind, kind, null, "installed");
  }
  for (const inst of byKey.values()) {
    inst.state = inst.entities.length
      ? worst(inst.entities.map((e) => e.state))
      : "unknown";
    // A silent box drags everything on it down (§4.3) — mirror the server.
    if (!box.connected) inst.state = "down";
    // Occupancy/job/warmTargets live on the runner's OWN `runner:<id>`
    // entity specifically (not the `identity:<id>` one this same instance
    // may also carry) — direct operator request, 2026-09-21.
    if (inst.kind === "runner") {
      const runnerEntity = inst.entities.find((e) =>
        e.entity.startsWith("runner:"),
      );
      const d = (runnerEntity?.detail ?? null) as Record<
        string,
        unknown
      > | null;
      if (d) {
        inst.occupancy = d["occupancy"] as Instance["occupancy"];
        inst.job = (d["job"] ?? null) as RunnerJob | null;
        inst.warmTargets = readBuckets(d["warmTargets"]);
        inst.warmTargetsVersion =
          typeof d["warmTargetsVersion"] === "string"
            ? d["warmTargetsVersion"]
            : null;
        // Only a live detail speaks for admission (a stale/silent entity's
        // detail is its staleness reason, without `admission`).
        inst.readiness =
          runnerEntity &&
          (runnerEntity.state === "up" || runnerEntity.state === "degraded")
            ? runnerReadiness(d)
            : null;
      }
    }
  }
  return [...byKey.values()];
}

/* ── Nodes ──────────────────────────────────────────────────────────────── */

interface ServiceData extends Record<string, unknown> {
  inst: Instance;
  selected: boolean;
  dimmed: boolean;
  highlighted: boolean;
}
type ServiceNodeT = Node<ServiceData, "service">;

const RING: Record<HealthState, string> = {
  up: "ring-green-600/70",
  degraded: "ring-yellow-500 animate-pulse",
  down: "ring-destructive animate-pulse",
  unknown: "ring-border",
};
const DOT: Record<HealthState, string> = {
  up: "bg-green-600",
  degraded: "bg-yellow-500",
  down: "bg-destructive",
  unknown: "bg-muted-foreground",
};
// Blue overrides the health-state color for a runner mid-prewarm (direct
// operator request, 2026-09-21: "Runner status should also be visible on
// the Fleet Flow page with blue color during prewarm phases") — distinct
// from `up`'s green so "warming up, not yet fully ready" reads at a
// glance, distinct from `degraded`'s amber since this is expected,
// routine activity, not an incident. `down` still wins over it (an
// actual outage is always the more urgent thing to show) — see
// isPrewarmingNode below.
const PREWARM_RING = "ring-blue-500 animate-pulse";
const PREWARM_DOT = "bg-blue-500";

/** True only for a runner node that's genuinely prewarming right now AND
 *  not simultaneously down — an outage always outranks "busy warming up"
 *  visually, same precedence health states already give `down` over
 *  everything else. */
function isPrewarmingNode(inst: Instance): boolean {
  if (inst.kind !== "runner" || inst.state === "down") return false;
  // Admission-aware runner: blue while starting / rolling out (intake
  // closed for routine warm-up), never for `degraded` (amber, an incident).
  if (inst.readiness) return inst.readiness.badgeVariant === "info";
  return inst.occupancy === "prewarming";
}

/** Node subtitle for an admission-aware runner. */
function readinessSub(r: RunnerReadiness): string {
  if (r.admission === "serving")
    return r.kind === "processing" && r.jobId
      ? `processing job ${r.jobId.slice(0, 8)}…`
      : "accepting jobs";
  const b = r.rollout?.buckets;
  const warm = b
    ? ` · warm ${b.done}/${b.total}${b.failed ? ` · ${b.failed} failed` : ""}`
    : "";
  const cur = b?.current ? ` · ${b.current}` : "";
  if (r.admission === "degraded") return "degraded · intake closed";
  return `${r.badge}${r.rollout ? ` · ${r.rollout.phase}` : ""}${cur}${warm}`;
}

function ServiceNode({ data }: NodeProps<ServiceNodeT>) {
  const { inst } = data;
  const meta = KIND_META[inst.kind];
  const label = meta?.label ?? inst.kind;
  const prewarming = isPrewarmingNode(inst);
  const warmSummary = inst.warmTargets?.length
    ? `warm ${inst.warmTargets.filter((b) => b.status === "warm").length}/${inst.warmTargets.length}`
    : null;
  const sub =
    inst.source === "installed"
      ? `installed ${inst.box.installed_components[inst.kind] ?? ""} · no telemetry`
      : inst.readiness
        ? readinessSub(inst.readiness)
        : inst.occupancy === "processing" && inst.job?.jobId
          ? `processing job ${inst.job.jobId.slice(0, 8)}…`
          : prewarming
            ? `prewarming${warmSummary ? ` · ${warmSummary}` : "…"}`
            : inst.key !== inst.kind && inst.key !== "agent"
              ? inst.key
              : `${inst.entities.length} signal${inst.entities.length === 1 ? "" : "s"}`;
  return (
    <div
      className={cn(
        "w-44 select-none rounded-lg border bg-card px-3 py-2 shadow-sm transition-all ring-1",
        prewarming ? PREWARM_RING : RING[inst.state],
        data.selected && "ring-2 ring-primary scale-105",
        data.highlighted && !data.selected && "ring-2 ring-primary/60",
        data.dimmed && !data.selected && !data.highlighted && "opacity-25",
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!h-1 !w-1 !border-0 !bg-transparent"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-1 !w-1 !border-0 !bg-transparent"
      />
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "h-2 w-2 shrink-0 rounded-full",
            prewarming ? PREWARM_DOT : DOT[inst.state],
          )}
        />
        <span className="truncate text-sm font-semibold">{label}</span>
      </div>
      <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
        {sub}
      </div>
    </div>
  );
}

interface ZoneData extends Record<string, unknown> {
  label: string;
  sublabel: string;
  state: HealthState;
}
type ZoneNodeT = Node<ZoneData, "zone">;
const ZONE_COLOR: Record<HealthState, string> = {
  up: "rgba(22,163,74,0.5)",
  degraded: "rgba(234,179,8,0.6)",
  down: "rgba(220,38,38,0.6)",
  unknown: "rgba(148,163,184,0.35)",
};
function ZoneNode({ data }: NodeProps<ZoneNodeT>) {
  return (
    <div
      className="pointer-events-none h-full w-full rounded-2xl border-2 border-dashed"
      style={{
        borderColor: ZONE_COLOR[data.state],
        background: "rgba(148,163,184,0.04)",
      }}
    >
      <div className="ml-4 mt-3 inline-block rounded-md bg-background px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider">
        {data.label}
        <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
          {data.sublabel}
        </span>
      </div>
    </div>
  );
}

const NODE_TYPES = { service: ServiceNode, zone: ZoneNode };

/* ── Layout: one row per box ────────────────────────────────────────────── */

const NODE_W = 176;
const NODE_H = 56;
const COL_GAP = 28;
const ROW_GAP = 20;
const ZONE_PAD_X = 36;
const ZONE_PAD_Y = 48;
const SECTION_GAP = 32;
const LEFT = 40;
const PER_ROW = 6;

const ZONE_ORDER: Zone[] = [
  "public",
  "control",
  "execution",
  "data",
  "identity",
];

function buildGraph(
  topo: Topology,
  selectedId: string | null,
  filter: "all" | "incidents",
  edgeKinds: ReadonlySet<EdgeKind>,
): { nodes: Node[]; edges: Edge[]; instances: Instance[] } {
  const instances: Instance[] = topo.boxes.flatMap(instancesFor);
  const byKind = new Map<string, Instance[]>();
  for (const i of instances)
    byKind.set(i.kind, [...(byKind.get(i.kind) ?? []), i]);

  // All edges from rules × instances; rendered only around the selection.
  const allEdges: Array<{ from: Instance; to: Instance; rule: Rule }> = [];
  for (const from of instances) {
    for (const rule of DEPENDENCY_RULES[from.kind] ?? []) {
      for (const to of byKind.get(rule.to) ?? [])
        allEdges.push({ from, to, rule });
    }
  }
  const neighbours = new Set<string>();
  if (selectedId) {
    for (const e of allEdges) {
      if (e.from.id === selectedId) neighbours.add(e.to.id);
      if (e.to.id === selectedId) neighbours.add(e.from.id);
    }
  }

  const nodes: Node[] = [];
  let y = 40;
  for (const box of topo.boxes) {
    const insts = instances
      .filter((i) => i.box.box_id === box.box_id)
      .sort(
        (a, b) =>
          ZONE_ORDER.indexOf(KIND_META[a.kind]?.zone ?? "data") -
            ZONE_ORDER.indexOf(KIND_META[b.kind]?.zone ?? "data") ||
          a.kind.localeCompare(b.kind) ||
          a.key.localeCompare(b.key),
      );
    const rows = Math.max(1, Math.ceil(insts.length / PER_ROW));
    const cols = Math.min(PER_ROW, Math.max(1, insts.length));
    const w = ZONE_PAD_X * 2 + cols * NODE_W + (cols - 1) * COL_GAP;
    const h = ZONE_PAD_Y + rows * NODE_H + (rows - 1) * ROW_GAP + 20;
    nodes.push({
      id: `zone/${box.box_id}`,
      type: "zone",
      position: { x: LEFT, y },
      style: { width: w, height: h },
      selectable: false,
      draggable: false,
      data: {
        label: box.hostname,
        sublabel: box.connected
          ? `${box.state} · agent ${box.last_seen_ago_s ?? "?"}s ago`
          : `DOWN · deploy-agent silent ${box.last_seen_ago_s !== null ? `${Math.round(box.last_seen_ago_s / 60)}m` : ""}`,
        state: box.state,
      } satisfies ZoneData,
      zIndex: -1,
    });
    insts.forEach((inst, idx) => {
      const r = Math.floor(idx / PER_ROW);
      const c = idx % PER_ROW;
      const operational = inst.state === "up";
      nodes.push({
        id: inst.id,
        type: "service",
        position: {
          x: LEFT + ZONE_PAD_X + c * (NODE_W + COL_GAP),
          y: y + ZONE_PAD_Y + r * (NODE_H + ROW_GAP),
        },
        data: {
          inst,
          selected: inst.id === selectedId,
          highlighted: neighbours.has(inst.id),
          dimmed:
            (filter === "incidents" && operational) ||
            (selectedId !== null && !neighbours.has(inst.id)),
        } satisfies ServiceData,
      });
    });
    y += h + SECTION_GAP;
  }

  const EDGE_STYLE: Record<EdgeKind, { stroke: string; dash?: string }> = {
    data: { stroke: "#3b82f6" },
    control: { stroke: "#f59e0b", dash: "6 4" },
    trust: { stroke: "#a855f7", dash: "2 4" },
  };
  const edges: Edge[] = selectedId
    ? allEdges
        .filter(
          (e) =>
            (e.from.id === selectedId || e.to.id === selectedId) &&
            edgeKinds.has(e.rule.kind),
        )
        .map((e, i) => ({
          id: `e${i}/${e.from.id}->${e.to.id}`,
          source: e.from.id,
          target: e.to.id,
          label: e.rule.label,
          labelStyle: { fontSize: 10 },
          labelBgStyle: { fillOpacity: 0.85 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: EDGE_STYLE[e.rule.kind].stroke,
          },
          style: {
            stroke: EDGE_STYLE[e.rule.kind].stroke,
            strokeDasharray: EDGE_STYLE[e.rule.kind].dash,
          },
          animated: e.rule.kind === "data",
        }))
    : [];
  return { nodes, edges, instances };
}

/* ── Detail pane ────────────────────────────────────────────────────────── */

function entityDetailLine(e: FleetEntity): string | null {
  const d = (e.detail ?? null) as Record<string, unknown> | null;
  if (!d) return null;
  if (
    e.entity.startsWith("runner:") &&
    (e.state === "up" || e.state === "degraded")
  ) {
    const r = runnerReadiness(d);
    if (r) return r.line;
  }
  if (typeof d["reason"] === "string") return d["reason"];
  if (Array.isArray(d["reasons"]) && d["reasons"].length > 0)
    return (d["reasons"] as string[]).join(" · ");
  const compiler = d["compiler"] as Record<string, unknown> | undefined;
  const err = compiler?.["error"] as Record<string, unknown> | undefined;
  if (err)
    return `${String(err["code"] ?? "")} · ${String(err["hint"] ?? err["summary"] ?? "")}`;
  if (typeof d["systemd"] === "string") return `systemd: ${d["systemd"]}`;
  if (typeof d["status"] === "string") return `status: ${d["status"]}`;
  return null;
}

const STATE_VARIANT: Record<
  HealthState,
  "default" | "secondary" | "destructive" | "success" | "warning"
> = {
  up: "success",
  degraded: "warning",
  down: "destructive",
  unknown: "secondary",
};

function DetailPane({
  inst,
  instances,
}: {
  inst: Instance | null;
  instances: Instance[];
}) {
  if (!inst) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Click a node to see its signals and what it depends on. Edges are drawn
        only for the selected node.
        <div className="mt-4 space-y-1 text-xs">
          <div>
            <span className="inline-block h-0.5 w-6 bg-blue-500 align-middle" />{" "}
            data
          </div>
          <div>
            <span className="inline-block h-0.5 w-6 border-t-2 border-dashed border-amber-500 align-middle" />{" "}
            control
          </div>
          <div>
            <span className="inline-block h-0.5 w-6 border-t-2 border-dotted border-purple-500 align-middle" />{" "}
            trust (toggle)
          </div>
        </div>
      </div>
    );
  }
  const meta = KIND_META[inst.kind];
  const deps = (DEPENDENCY_RULES[inst.kind] ?? []).map((r) => ({
    rule: r,
    targets: instances.filter((i) => i.kind === r.to),
  }));
  const dependants = instances.filter((i) =>
    (DEPENDENCY_RULES[i.kind] ?? []).some((r) => r.to === inst.kind),
  );
  return (
    <div className="space-y-4 p-4 text-sm">
      <div>
        <div className="flex items-center gap-2">
          {inst.readiness ? (
            <Badge
              variant={inst.readiness.badgeVariant}
              title={`health: ${inst.state} · admission: ${inst.readiness.admission}`}
            >
              {inst.readiness.badge}
            </Badge>
          ) : (
            <Badge variant={STATE_VARIANT[inst.state]}>{inst.state}</Badge>
          )}
          <span className="font-semibold">{meta?.label ?? inst.kind}</span>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          on{" "}
          <Link
            className="underline"
            to={`/fleet?box=${encodeURIComponent(inst.box.box_id)}`}
          >
            {inst.box.hostname}
          </Link>
          {inst.key !== inst.kind && inst.key !== "agent"
            ? ` · ${inst.key}`
            : ""}
        </div>
        {!inst.box.connected && (
          <div className="mt-2 rounded border border-destructive/40 bg-destructive/10 p-2 text-xs">
            deploy-agent on this box is silent — everything on it is treated as
            down (§4.3).
          </div>
        )}
        {inst.occupancy === "processing" && inst.job?.jobId && (
          <div className="mt-2 rounded border border-blue-500/40 bg-blue-500/10 p-2 text-xs">
            processing job <span className="font-mono">{inst.job.jobId}</span>
          </div>
        )}
      </div>
      {inst.readiness && inst.readiness.admission !== "serving" && (
        <div
          className={cn(
            "rounded border p-2 text-xs",
            inst.readiness.admission === "degraded"
              ? "border-yellow-500/40 bg-yellow-500/10"
              : "border-blue-500/40 bg-blue-500/10",
          )}
        >
          <div className="font-semibold">
            {inst.readiness.badge} — not accepting jobs
          </div>
          {inst.readiness.reason && (
            <div className="mt-0.5">{inst.readiness.reason}</div>
          )}
          {inst.readiness.progress && (
            <div className="mt-0.5 text-muted-foreground">
              {inst.readiness.progress}
            </div>
          )}
          {inst.readiness.backoffText && (
            <div className="mt-0.5 text-yellow-700 dark:text-yellow-400">
              {inst.readiness.backoffText}
            </div>
          )}
          {inst.readiness.rollout?.buckets &&
            inst.readiness.rollout.buckets.buckets.length > 0 && (
              <div className="mt-1">
                <WarmBucketList
                  buckets={inst.readiness.rollout.buckets.buckets}
                  layout="list"
                />
              </div>
            )}
        </div>
      )}
      {inst.readiness &&
        inst.readiness.admission === "serving" &&
        inst.readiness.backoffText && (
          <div className="rounded border border-yellow-500/40 bg-yellow-500/10 p-2 text-xs">
            <div className="font-semibold">staged rollout failed</div>
            <div className="mt-0.5">{inst.readiness.backoffText}</div>
          </div>
        )}
      {inst.kind === "runner" &&
        inst.warmTargets &&
        inst.warmTargets.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Warm targets
              {inst.warmTargetsVersion ? ` · v${inst.warmTargetsVersion}` : ""}
            </div>
            <div className="text-xs">
              <WarmBucketList buckets={inst.warmTargets} layout="list" />
            </div>
          </div>
        )}
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Signals
        </div>
        {inst.entities.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            {inst.source === "installed"
              ? "Installed by pure-deploy but nothing has reported on it. Green here would be a guess — so it isn't."
              : "No telemetry."}
          </div>
        ) : (
          <ul className="space-y-1.5">
            {inst.entities
              .slice()
              .sort((a, b) => RANK[b.state] - RANK[a.state])
              .map((e) => (
                <li key={e.entity} className="text-xs">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn("h-1.5 w-1.5 rounded-full", DOT[e.state])}
                    />
                    <span className="font-mono">{e.entity}</span>
                  </div>
                  {entityDetailLine(e) && (
                    <div className="ml-3.5 text-muted-foreground">
                      {entityDetailLine(e)}
                    </div>
                  )}
                </li>
              ))}
          </ul>
        )}
      </div>
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Depends on
        </div>
        {deps.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            nothing in the platform
          </div>
        ) : (
          <ul className="space-y-1 text-xs">
            {deps.map(({ rule, targets }) => (
              <li key={rule.to}>
                <span className="font-medium">
                  {KIND_META[rule.to]?.label ?? rule.to}
                </span>
                <span className="text-muted-foreground"> — {rule.label}</span>
                {targets.length === 0 ? (
                  <span className="ml-1 text-destructive">
                    no instance anywhere
                  </span>
                ) : (
                  <span className="ml-1">
                    {targets.map((t) => (
                      <Badge
                        key={t.id}
                        variant={STATE_VARIANT[t.state]}
                        className="ml-1"
                      >
                        {t.box.hostname}
                      </Badge>
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {dependants.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Depended on by
          </div>
          <div className="flex flex-wrap gap-1">
            {dependants.map((d) => (
              <Badge key={d.id} variant={STATE_VARIANT[d.state]}>
                {KIND_META[d.kind]?.label ?? d.kind} @ {d.box.hostname}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

export function FleetFlowPage() {
  const token = useAuthToken();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("node");
  const filter = (params.get("filter") as "all" | "incidents" | null) ?? "all";
  const showTrust = params.get("trust") === "1";
  const [topo, setTopo] = useState<Topology | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setTopo(await api<Topology>(token, "/fleet/topology"));
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "failed to load topology",
      );
    }
  }, [token]);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 15_000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(
    () => (token ? onDashboardChanged(token, () => void load()) : undefined),
    [token, load],
  );

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v === null) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  const edgeKinds = useMemo(
    () =>
      new Set<EdgeKind>(
        showTrust ? ["data", "control", "trust"] : ["data", "control"],
      ),
    [showTrust],
  );
  const graph = useMemo(
    () => (topo ? buildGraph(topo, selectedId, filter, edgeKinds) : null),
    [topo, selectedId, filter, edgeKinds],
  );
  const selected = graph?.instances.find((i) => i.id === selectedId) ?? null;
  const incidents =
    graph?.instances.filter((i) => i.state === "down" || i.state === "degraded")
      .length ?? 0;

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
        <h1 className="mr-2 text-base font-semibold">Fleet Flow</h1>
        <Button
          size="sm"
          variant={filter === "all" ? "default" : "outline"}
          onClick={() => setParam("filter", null)}
        >
          All
        </Button>
        <Button
          size="sm"
          variant={filter === "incidents" ? "default" : "outline"}
          onClick={() => setParam("filter", "incidents")}
        >
          Incidents · {incidents}
        </Button>
        <span className="mx-1 h-4 w-px bg-border" />
        <Button
          size="sm"
          variant={showTrust ? "default" : "outline"}
          onClick={() => setParam("trust", showTrust ? null : "1")}
        >
          Trust edges
        </Button>
        <span className="mx-1 h-4 w-px bg-border" />
        {topo?.capabilities.map((c) => (
          <Badge key={c.name} variant={STATE_VARIANT[c.state]} title={c.reason}>
            {c.name}
          </Badge>
        ))}
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <Link className="underline" to="/fleet">
            Boxes view
          </Link>
          {topo && (
            <span>
              updated {new Date(topo.generated_at).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
      {error && (
        <div className="border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-sm">
          {error}
        </div>
      )}
      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[1fr_360px]">
        <div className="min-h-[320px]">
          {graph ? (
            <ReactFlowProvider>
              <ReactFlow
                nodes={graph.nodes}
                edges={graph.edges}
                nodeTypes={NODE_TYPES}
                fitView
                minZoom={0.2}
                nodesDraggable={false}
                nodesConnectable={false}
                onNodeClick={(_, n) =>
                  n.type === "service" &&
                  setParam("node", n.id === selectedId ? null : n.id)
                }
                onPaneClick={() => setParam("node", null)}
                proOptions={{ hideAttribution: true }}
              >
                <Background gap={24} />
                <Controls showInteractive={false} />
              </ReactFlow>
            </ReactFlowProvider>
          ) : (
            <div className="p-4 text-sm text-muted-foreground">
              Loading topology…
            </div>
          )}
        </div>
        <div className="overflow-y-auto border-l">
          <DetailPane inst={selected} instances={graph?.instances ?? []} />
        </div>
      </div>
    </div>
  );
}
